import SendIcon from "@mui/icons-material/Send";
import {
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Stack,
	TextField,
} from "@mui/material";
import { type FC, useCallback, useState } from "react";
import type { AttendanceStatus, EventRecord } from "@/types/event-attendance";
import { validateEmail } from "@/frontend/utils";

const SignupSubmitButton: FC<{
	disabled: boolean;
	onClick: () => void;
}> = ({ disabled, onClick }) => (
	<Box
		sx={{
			p: 2,
			width: "100%",
			display: "flex",
			justifyContent: "center",
			mt: 2,
		}}
	>
		<Button
			variant="contained"
			size="large"
			startIcon={<SendIcon />}
			onClick={onClick}
			disabled={disabled}
			sx={{ minWidth: 200 }}
			data-testid="register-submit"
		>
			Abschicken und registrieren
		</Button>
	</Box>
);

const DuplicateEmailDialog: FC<{
	open: boolean;
	onClose: () => void;
	onConfirmLogin: () => void;
}> = ({ open, onClose, onConfirmLogin }) => (
	<Dialog open={open} onClose={onClose}>
		<DialogTitle>E-Mail bereits registriert</DialogTitle>
		<DialogContent>
			<DialogContentText>
				Diese E-Mail ist bereits registriert. Bitte logge dich ein.
			</DialogContentText>
		</DialogContent>
		<DialogActions>
			<Button onClick={onConfirmLogin} autoFocus>
				OK
			</Button>
		</DialogActions>
	</Dialog>
);

export const SignupCard: FC<{
	events: EventRecord[];
	localAttendance: Record<number, AttendanceStatus>;
	authClient: ReturnType<typeof import("better-auth/client").createAuthClient>;
	onSyncLocalToBackend: () => Promise<void>;
	onErrorMsg: (msg: string) => void;
	onNavigateLogin: () => void;
}> = ({
	events,
	localAttendance,
	authClient,
	onSyncLocalToBackend,
	onErrorMsg,
	onNavigateLogin,
}) => {
	const [signupEmail, setSignupEmail] = useState("");
	const [signupNickname, setSignupNickname] = useState("");
	const [signupSubmitting, setSignupSubmitting] = useState(false);
	const [duplicateEmailDialogOpen, setDuplicateEmailDialogOpen] =
		useState(false);
	const [emailError, setEmailError] = useState<string | null>(null);
	const [nicknameError, setNicknameError] = useState<string | null>(null);

	const validateNickname = useCallback((value: string): string | null => {
		const trimmed = value.trim();
		if (!trimmed) return "Bitte einen Namen eingeben";
		if (trimmed.length < 2) return "Name ist zu kurz";
		return null;
	}, []);

	const validateSignupFields = useCallback((): boolean => {
		const eErr = validateEmail(signupEmail);
		const nErr = validateNickname(signupNickname);
		setEmailError(eErr);
		setNicknameError(nErr);
		return !eErr && !nErr;
	}, [signupEmail, signupNickname, validateNickname]);

	const handleSignupEmailChange = useCallback(
		(v: string) => {
			setSignupEmail(v);
			if (emailError) setEmailError(null);
		},
		[emailError],
	);

	const handleSignupNicknameChange = useCallback(
		(v: string) => {
			setSignupNickname(v);
			if (nicknameError) setNicknameError(null);
		},
		[nicknameError],
	);

	const handleSignup = useCallback(async () => {
		try {
			const allEventsHaveAttendance = events.every(
				(ev) => localAttendance[ev.id] !== undefined,
			);
			if (!allEventsHaveAttendance) {
				onErrorMsg("Bitte zuerst für alle Termine eintragen");
				return;
			}

			if (!validateSignupFields()) {
				onErrorMsg("Bitte korrigiere die Eingabefehler oben.");
				window.scrollTo(0, 0);
				return;
			}

			setSignupSubmitting(true);
			const pwd = crypto.getRandomValues(new Uint32Array(4)).join("");
			const res = await authClient.signUp.email({
				email: signupEmail,
				password: pwd,
				name: signupNickname || signupEmail,
			});

			const code = res.error?.code ?? res.error?.code ?? undefined;
			if (code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL") {
				setEmailError("Diese E-Mail ist bereits registriert.");
				setDuplicateEmailDialogOpen(true);
				return;
			}
			if (res?.error?.message) {
				throw new Error(res.error.message);
			}

			await authClient.signIn.email({ email: signupEmail, password: pwd });
			await onSyncLocalToBackend();
			window.scrollTo(0, 0);

			try {
				await authClient.forgetPassword.emailOtp({ email: signupEmail });
			} catch (e) {
				console.error("Failed to send forget password OTP", e);
			}
		} catch (e) {
			const msg = (e as Error)?.message;
			const lower = (msg || "").toLowerCase();
			const thrownCode =
				(e as { code?: string; error?: { code?: string } })?.code ||
				(e as { code?: string; error?: { code?: string } })?.error?.code;
			if (thrownCode === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL") {
				setEmailError("Diese E-Mail ist bereits registriert.");
				setDuplicateEmailDialogOpen(true);
				return;
			}

			if (
				(lower.includes("already") ||
					lower.includes("exist") ||
					lower.includes("in use") ||
					lower.includes("registr")) &&
				(lower.includes("email") || lower.includes("user"))
			) {
				const friendly =
					"Diese E-Mail ist bereits registriert. Bitte logge dich ein.";
				setEmailError(friendly);
				setDuplicateEmailDialogOpen(true);
				return;
			}

			if (msg) {
				let mapped = false;
				if (lower.includes("mail") || lower.includes("email")) {
					setEmailError(msg);
					mapped = true;
				}
				if (
					lower.includes("name") ||
					lower.includes("username") ||
					lower.includes("nick")
				) {
					setNicknameError(msg);
					mapped = true;
				}
				if (mapped) return;
			}
			onErrorMsg(msg ?? "Registrierung fehlgeschlagen");
		} finally {
			setSignupSubmitting(false);
		}
	}, [
		events,
		localAttendance,
		validateSignupFields,
		authClient,
		signupEmail,
		signupNickname,
		onSyncLocalToBackend,
		onErrorMsg,
	]);

	return (
		<>
			<Card variant="elevation" sx={{ bgcolor: "rgba(255,255,255,0.7)" }}>
				<Box sx={{ p: 2 }}>
					<Stack spacing={2}>
						<TextField
							inputProps={{ "data-testid": "register-name" }}
							label="Name"
							fullWidth
							value={signupNickname}
							onChange={(e) => handleSignupNicknameChange(e.target.value)}
							error={!!nicknameError}
							helperText={nicknameError || ""}
						/>
						<TextField
							inputProps={{ "data-testid": "register-email" }}
							label="E-Mail"
							type="email"
							fullWidth
							value={signupEmail}
							onChange={(e) => handleSignupEmailChange(e.target.value)}
							error={!!emailError}
							helperText={emailError || ""}
						/>
					</Stack>
				</Box>
			</Card>

			<SignupSubmitButton disabled={signupSubmitting} onClick={handleSignup} />

			<DuplicateEmailDialog
				open={duplicateEmailDialogOpen}
				onClose={() => setDuplicateEmailDialogOpen(false)}
				onConfirmLogin={() => {
					setDuplicateEmailDialogOpen(false);
					onNavigateLogin();
				}}
			/>
		</>
	);
};
